import SelectedBlokOverlay from './SelectedBlokOverlay';
import HoverBlokOverlay from './HoverBlokOverlay';
import BlokManager from './BlokManager';
import store from '../../../GameKitUI/store';

import { setActiveBlock, updateSettings, updateStateLook, setState } from '../../../GameKitUI/actions/blokEditorActions';
import { setActiveSidebarItem, activateSidebar } from '../../../GameKitUI/actions/sideBarActions';
import { updateActionReactionSettings } from '../../../GameKitUI/actions/actionReactionActions';

import SelectedStateOverlay from './SelectedStateOverlay';
import State, { AppearanceTypes } from '../../../Game/Level/Blok/State';
import Pattern from '../../../Game/Level/Blok/Appearance/Pattern';
import Text from '../../../Game/Level/Blok/Appearance/Text';
import LibraryManager from '../../../Game/Base/Library/LibraryManager';
import Image from '../../../Game/Level/Blok/Appearance/Image';
import SelectedActieReactieOverlay from './SelectedActieReactieOverlay';
import { ReactionAfterwards, MovementBehaviour, ResizeBehaviour, RotationBehaviour } from '../../shared';
import { SideBarItems } from '../../../GameKitUI/shared/index';
import Angle from '../../../Game/Base/Math/Angle';
import Blok from '../../../Game/Level/Blok';
import Vector from '../../../Game/Base/Math/Vector';

export default class BlokEditor {
    constructor(levelEditor) {
        this.levelEditor = levelEditor;
        this.gameKit = this.levelEditor.gameKit;

        this.blokManager = new BlokManager(this);

        this.hoverBlokOverlay = new HoverBlokOverlay(this);
        this.selectedStateOverlay = new SelectedStateOverlay(this);
        this.selectedBlokOverlay = new SelectedBlokOverlay(this);
        this.selectedActieReactieOverlay = new SelectedActieReactieOverlay(this);

        this._selectedBlok = null;
        this._hoveredBlok = null;
        this._selectedState = 0;

        this.selectingBlokForActieReactie = false;
        this.inStateMode = true;
    }

    attachToLevel(level) {
        this.blokManager.detachFromStepParent();
        this.hoverBlokOverlay.detachFromStepParent();
        this.selectedStateOverlay.detachFromStepParent();
        this.selectedBlokOverlay.detachFromStepParent();
        this.selectedActieReactieOverlay.detachFromStepParent();
        this.blokManager.attachToStepParent(level);
        if (this._hoveredBlok) this.hoverBlokOverlay.attachToStepParent(this._hoveredBlok);
        if (this._selectedBlok) {
            if (this.inStateMode) {
                this.selectedStateOverlay.attachToStepParent(this._selectedBlok);
                this.selectedBlokOverlay.attachToStepParent(this._selectedBlok);
                store.dispatch(setActiveBlock(this._selectedBlok.uuid));
                store.dispatch(activateSidebar());
            } else {
                const actieReactie = this.getSelectedStateActieReactie();
                if (actieReactie) {
                    this.selectedActieReactieOverlay.attachToStepParent(this._selectedBlok);
                }
            }
            store.dispatch(activateSidebar());
        }
    }

    detachFromLevel() {
        store.dispatch(setActiveSidebarItem(SideBarItems.NONE));
        this.blokManager.detachFromStepParent();
        this.hoverBlokOverlay.detachFromStepParent();
        this.selectedBlokOverlay.detachFromStepParent();
        this.selectedStateOverlay.detachFromStepParent();
        this.selectingBlokForActieReactie = false;
        this.setSelectedBlok(null, false);
    }

    createNewBlok() {
        return this.blokManager.createNewBlok();
    }

    spawnPrefab(prefab, x = null, y = null) {
        this.switchToActieReactieMode(false);
        const { level } = this.levelEditor;
        if (x !== null && y !== null) {
            const res = this.gameKit.game.canvasToGamePosition(new Vector(x, y));
            x = res.x;
            y = res.y;
        }
        if (x === null) x = level.view.x;
        if (y === null) y = level.view.y;
        const newBlok = new Blok(level);
        newBlok.position.x = x;
        newBlok.position.y = y;
        try {
            const lib = LibraryManager.prefabs.get(prefab);
            const prefabData = lib.get();
            newBlok.deserialize(prefabData);
            newBlok.name = lib.name;
        } catch {
            console.error(`Prefab with ID ${prefab} could not be found.`);
        }

        level.blokken.push(newBlok);
        if (level._hasVisuals) newBlok._gsoCreateVisuals();
        this.setSelectedBlok(newBlok);
        LibraryManager.addRecentPrefab(prefab);
        return newBlok;
    }

    // Start or select a blok to forward an action reaction to.
    // Calling this without a parameter will iniate the selection of another blok.
    // Calling it with a parameter will end the selection and set it to the given blok in the parameters.
    selectActionReactionForward(otherBlok = null) {
        const actieReactie = this.getSelectedStateActieReactie();
        if (!actieReactie) throw new Error('Failed to retrieve actie reactie. Either block, or state was not selected.');
        if (otherBlok === this.getSelectedBlok()) {
            actieReactie.passOnReaction = '';
            this.selectingBlokForActieReactie = false;
            this.selectedActieReactieOverlay.onUpdatePassOnReaction();
        } else if (otherBlok) {
            actieReactie.passOnReaction = otherBlok.uuid;
            this.selectingBlokForActieReactie = false;
            this.selectedActieReactieOverlay.onUpdatePassOnReaction();
        } else {
            this.selectingBlokForActieReactie = true;
        }
    }

    setHoveredBlok(blok) {
        if (this.hoverBlokOverlay.blockDrag.state === 2) return;
        if (blok !== this._hoveredBlok) {
            if (this._hoveredBlok != null) {
                this.hoverBlokOverlay.detachFromStepParent();
            }
            if (blok != null) {
                this.hoverBlokOverlay.attachToStepParent(blok);
            }
            this._hoveredBlok = blok;
        }
    }

    getHoveredBlok() {
        return this._hoveredBlok;
    }

    switchToActieReactieMode(actieReactieMode) {
        const blok = this.getSelectedBlok();
        this.setSelectedBlok(null, false);
        this.inStateMode = !actieReactieMode;
        this.setSelectedBlok(blok, false); // true);
    }

    setSelectedBlok(blok, dispatch = true) {
        if (blok !== this._selectedBlok) {
            if (this._selectedBlok) {
                this.setSelectedBlokState(0);
                this._selectedBlok.setActiveState(0);
            }
            this._selectedBlok = blok;
            this.selectedBlokOverlay.detachFromStepParent();
            this.selectedStateOverlay.detachFromStepParent();
            this.selectedActieReactieOverlay.detachFromStepParent();
        } else {
            return; // No change so let's return.
        }
        if (blok != null) {
            this.blokManager.putBlokAtTop(blok);
            if (this.inStateMode) {
                this.selectedStateOverlay.attachToStepParent(blok);
                this.selectedBlokOverlay.attachToStepParent(blok);
            } else {
                const actieReactie = this.getSelectedStateActieReactie();
                if (actieReactie) {
                    this.selectedActieReactieOverlay.attachToStepParent(this._selectedBlok);
                }
            }
        } else {
            this.selectingBlokForActieReactie = false;
            this.inStateMode = true;
        }

        if (dispatch) {
            // Update de store
            if (this._selectedBlok) {
                // Dispatch blokeditor states
                store.dispatch(setActiveBlock(this._selectedBlok.uuid));
                store.dispatch(activateSidebar());
                store.dispatch(updateSettings(this.getSelectedBlokSettings()));
                store.dispatch(updateSettings(this.getSelectedBlokStateSelectedSettings()));
                store.dispatch(updateStateLook(this.getSelectedBlokStateLook()));

                this.setSelectedBlokState(0);

                // Dispatch action reaction states
                store.dispatch(updateActionReactionSettings(this.getSelectedBlokStateSelectedActionReactionSettings()));
            } else {
                store.dispatch(setActiveSidebarItem(SideBarItems.NONE));
            }
        }
    }

    getSelectedBlok() {
        return this._selectedBlok;
    }

    getSelectedStateActieReactie() {
        const blok = this.getSelectedBlok();
        if (!blok) return null;
        return blok.actieReacties[this._selectedState];
    }

    getSelectedBlokSettings() {
        return {
            shape: this._selectedBlok.shape,
            name: this._selectedBlok.name,
            state1Look: this.getBlokStateLookOfState(this._selectedBlok.states[0]),
            state2Look: this.getBlokStateLookOfState(this._selectedBlok.states[1])
        };
    }

    getBlokStateLookOfState(state) {
        if (!state) return {};
        if (state.appearanceType === 2) {
            return {
                type: 1,
                text: state.appearance.text,
                color: state.appearance.color,
                size: state.appearance.size
            };
        }
        return {
            type: 0,
            value: state.appearance.texture,
            hue: state.appearance.hue
        };
    }

    getSelectedBlokStateLook() {
        const state = this.getSelectedBlokSelectedState();
        return this.getBlokStateLookOfState(state);
    }

    getSelectedBlokStateSelectedSettings() {
        const state = this.getSelectedBlokSelectedState();
        return {
            thingsMoveThroughMe: state.thingsMoveThroughMe,
            friction: state.friction,
            bounce: state.bounce,
            weight: state.weight,

            speed: state.speed,
            movementBehaviour: state.movementBehaviour,

            rotationBehaviour: state.rotationBehaviour,
            rotationDirection: state.rotationDirection,
            rotationSpeed: state.rotationSpeed,
            rotationAngle: Math.round(Angle.toDegrees(state.rotationAngle)),

            resizeBehaviour: state.resizeBehaviour,
            resizeSpeed: state.resizeSpeed,
            resizeWidth: state.resizeWidth,
            resizeHeight: state.resizeHeight,

            changesBack: state.changesBack,
            changeBackTime: state.changeBackTime
        };
    }

    getSelectedBlokSelectedState() {
        const selectedBlok = this.getSelectedBlok();
        if (!selectedBlok) {
            console.error("Can't get blok's selected state settings. No blok was selected.");
            return;
        }
        const state = selectedBlok.states[this._selectedState];
        if (!state) {
            console.error("Can't get blok's selected state settings. State index does not match with a state.");
            return;
        }
        return state;
    }

    getSelectedBlokStateSelectedActionReactionSettings() {
        const selectedBlok = this.getSelectedBlok();
        if (!selectedBlok) {
            console.error("Can't get blok's selected state settings. No blok was selected.");
            return null;
        }
        const actieReactie = this.getSelectedStateActieReactie();
        if (!actieReactie) {
            console.error("Can't get blok's selected actie reactie settings. State index does not match with a actie reactie.");
            return null;
        }
        return {
            respondToPlayerCollision: actieReactie.respondToPlayerCollision,
            respondToObjectCollision: actieReactie.respondToObjectCollision,
            points: actieReactie.points,
            health: actieReactie.health,
            sound: actieReactie.sound,
            passOnReaction: actieReactie.passOnReaction,
            toLevel: actieReactie.toLevel,
            afterwards: actieReactie.afterwards
        };
    }

    // = // UI COMMUNICATION FUNCTIONS // = //

    // Set the selected blok by UUID. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    setSelectedBlokByUUID(uuid, dispatch = true) {
        if (uuid == null) this.setSelectedBlok(null, dispatch);
        const blok = this.blokManager.getBlokByUUID(uuid);
        if (!blok) return;
        this.setSelectedBlok(blok, dispatch);
    }

    // Close selected blok
    closeSelectedBlok() {
        this.setSelectedBlok(null);
    }

    // Set the selected blok's selected state. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    // stateIndex parameter: number (0: normaal, 1: anders)
    setSelectedBlokState(stateIndex, dispatch = true) {
        const selectedBlok = this.getSelectedBlok();
        if (!selectedBlok) {
            console.error("Can't set blok's active state. No blok was selected.");
            return;
        }
        const state = selectedBlok.states[stateIndex];
        if (!state) {
            console.error("Can't set blok's active state. State index does not match with a state.");
            return;
        }
        this._selectedState = stateIndex;
        this.selectedStateOverlay.detachFromStepParent();
        this.selectedBlokOverlay.attachToStepParent(selectedBlok);
        selectedBlok.setActiveState(stateIndex);
        this.selectedStateOverlay.attachToStepParent(selectedBlok);
        this.selectedBlokOverlay.attachToStepParent(selectedBlok);

        if (dispatch) {
            // Update de store
            store.dispatch(setState(this._selectedState));
            store.dispatch(updateSettings(this.getSelectedBlokSettings()));
            store.dispatch(updateSettings(this.getSelectedBlokStateSelectedSettings()));
            store.dispatch(updateStateLook(this.getSelectedBlokStateLook()));
        }
    }

    // Update the selected blok's settings.Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    // Settings parameter: {
    // shape: enum-number-Shape (from Blok.js)
    // }
    updateBlokSettings(settings, dispatch = true) {
        const selectedBlok = this.getSelectedBlok();
        if (!selectedBlok) {
            console.error("Can't set blok state settings. No blok was selected.");
            return;
        }

        if ('shape' in settings) selectedBlok.shape = settings.shape;

        selectedBlok.requestNewVisuals();
        this.selectedBlokOverlay.detachFromStepParent();
        this.selectedBlokOverlay.attachToStepParent(selectedBlok);

        if (dispatch) {
            // Update de store
            store.dispatch(updateSettings(this.getSelectedBlokSettings()));
        }
    }

    // Update the selected blok's selected state. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    // type: number (0: texture, 1: text),
    // Data parameter: {
    // value: string (ImageID for type==0, Text for type==1),
    // color: number/string (Hue rotation for type==0, Color value for type==1)
    // size: number (Text size for type==1)
    // }
    updateBlokStateLook(type, data, dispatch = true) {
        const state = this.getSelectedBlokSelectedState();

        const currentType = state.appearanceType;

        let newType = type;
        if (newType === 1) newType = 2;
        else if (data.value) {
            if (data.value === '') {
                newType = 0;
            } else {
                const texture = LibraryManager.textures.get(data.value);
                if (texture.stretch) newType = 0;
                else newType = 1;
            }
        }

        if (newType === 0) {
            if (currentType !== newType) {
                state.appearance.destroy();
                state.appearance = new Image(state);
                state.appearanceType = AppearanceTypes.indexOf(Image);
            }
            if (data.value) state.appearance.texture = data.value;
            if ('hue' in data) state.appearance.hue = data.hue;
            state.blok.requestNewVisuals();
        } else if (newType === 1) {
            if (currentType !== newType) {
                state.appearance.destroy();
                state.appearance = new Pattern(state);
                state.appearanceType = AppearanceTypes.indexOf(Pattern);
            }
            if (data.value) state.appearance.texture = data.value;
            if (data.hue) state.appearance.hue = data.hue;
            state.blok.requestNewVisuals();
        } else {
            if (currentType !== newType) {
                state.appearance.destroy();
                state.appearance = new Text(state);
                state.appearanceType = AppearanceTypes.indexOf(Text);
            }
            if (typeof data.text !== 'undefined') state.appearance.text = data.text;
            if (typeof data.color !== 'undefined') state.appearance.color = data.color;
            if (data.size) state.appearance.size = data.size;
            state.blok.requestNewVisuals();
        }

        if (dispatch) {
            // Update de store
            store.dispatch(updateStateLook(this.getSelectedBlokStateLook()));
            store.dispatch(updateSettings(this.getSelectedBlokSettings()));
        }
    }

    // Update the selected blok's selected state settings. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    // Settings parameter: {
    //      General
    // thingsMoveThroughMe: boolean,
    // friction: number (0-1),
    // bounce: number (0-1),
    // weight: number (0-1),

    //      Movement
    // speed: number,
    // movementBehaviour: enum-number-MovementBehaviour (from State.js),

    //      Rotation
    // rotationBehaviour: enum-number-RotationBehaviour (from State.js),
    // rotationDirection: enum-number-RotationDirection (from State.js),
    // rotationSpeed: number,
    // rotationAngle: radians-number,

    //      Resize
    // resizeBehaviour: enum-number-ResizeBehaviour (from State.js),
    // resizeSpeed: number,
    // resizeWidth: scale-number,
    // resizeHeight: scale-number

    //      Blijf ik anders?
    // changesBack: boolean
    // changeBackTime: number seconds
    // }
    updateBlokStateSettings(settings, dispatch = true) {
        const state = this.getSelectedBlokSelectedState();

        const acceptedSettings = [
            'thingsMoveThroughMe',
            'friction',
            'bounce',
            'weight',
            'speed',
            'movementBehaviour',
            'rotationBehaviour',
            'rotationDirection',
            'rotationSpeed',
            'rotationAngle',
            'resizeBehaviour',
            'resizeSpeed',
            'resizeWidth',
            'resizeHeight',
            'changesBack',
            'changeBackTime'
        ];
        for (let i = 0; i < acceptedSettings.length; i++) {
            if (acceptedSettings[i] in settings) {
                state[acceptedSettings[i]] = settings[acceptedSettings[i]];
                if (acceptedSettings[i] === 'rotationAngle') {
                    state[acceptedSettings[i]] = Angle.toRadians(state[acceptedSettings[i]]);
                }
            }
        }

        if (dispatch) {
            // Update de store
            store.dispatch(updateSettings(this.getSelectedBlokStateSelectedSettings()));
        }
    }

    // Update the selected blok's selected state actie reactie settings. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    // Settings parameter: {
    //      respondToPlayerCollision: boolean
    //      respondToObjectCollision: boolean
    //      points: number
    //      health: number
    //      sound: string (library ID or empty)
    //      passOnReaction: string (Blok UUID or empty)
    //      toLevel: string ("next", Level UUID or empty)
    //      afterwards: enum ReactionAfterwards from ActieReactie.js,
    // }
    updateBlokStateActionReaction(settings = {}, dispatch = true) {
        const actieReactie = this.getSelectedStateActieReactie();
        if ('respondToPlayerCollision' in settings) {
            actieReactie.respondToPlayerCollision = Boolean(settings.respondToPlayerCollision);
        }
        if ('respondToObjectCollision' in settings) {
            actieReactie.respondToObjectCollision = Boolean(settings.respondToObjectCollision);
        }
        if ('points' in settings) {
            actieReactie.points = Number(settings.points);
        }
        if ('health' in settings) {
            actieReactie.health = Number(settings.health);
        }
        if ('sound' in settings) {
            actieReactie.sound = settings.sound;
        }
        if ('passOnReaction' in settings) {
            actieReactie.passOnReaction = settings.passOnReaction;
        }
        if ('toLevel' in settings) {
            if (settings.toLevel === false) actieReactie.toLevel = '';
            else actieReactie.toLevel = settings.toLevel;
        }
        if ('afterwards' in settings) {
            actieReactie.afterwards = settings.afterwards;

            const blok = this.getSelectedBlok();
            if (actieReactie.afterwards === ReactionAfterwards.ChangeState) {
                const newState = new State(blok);
                newState.deserialize(blok.states[this._selectedState].serialize());
                newState.movementBehaviour = MovementBehaviour.none;
                newState.rotationBehaviour = RotationBehaviour.none;
                newState.resizeBehaviour = ResizeBehaviour.none;
                blok.states.push(newState);

                store.dispatch(updateSettings(this.getSelectedBlokSettings()));

                blok.states[this._selectedState + 1].disable();
            } else if (blok.states[this._selectedState + 1]) {
                blok.states[this._selectedState + 1].destroy();
                blok.states.splice(this._selectedState + 1, 1);
            }
        }

        if (dispatch) {
            // Update de store
            store.dispatch(updateActionReactionSettings(this.getSelectedBlokStateSelectedActionReactionSettings()));
        }
    }
}
